import type {
  AccuseCategoryVO,
  AccusePosterVo,
  AdminAccuseListVo,
  AdminPosterDetailVo,
  AdminPosterListVo,
  AdminPosterReplyDetailVo,
  AdminPosterReplyListVo,
  AdminProblemSolutionListVo,
  AdminSolutionInlineCommentDetailVo,
  AdminSolutionInlineCommentListVo,
  AdminSolutionInlineCommentPrevNextVo,
  AdminUserListVo,
  CommonVO,
  ContentHistoryRecordDetail,
  MessageViewVo,
  PosterHistoryRecordVO,
  ProblemSolutionAppealAuditVo,
  ProblemSolutionAuditVO,
  ProblemSolutionStatusFlowVo,
  SolutionInlineCommentAuditVo,
  UserMessageVo,
} from '@core/types/auto/forum';
import type { Pagination } from '@core/types/common';
import type {
  AdminSolutionInlineCommentQuery,
  PostListRequestVO,
  ReplyListRequestVO,
  SolutionListRequestVO,
} from '@core/types/forum';
import { httpService } from '../http';

/**
 * 封禁用户
 */
export function blockUser(id: string) {
  return httpService.post(
    `/api/code-community-forum/api/admin/poster/block/${id}`,
  );
}

/**
 * 解封用户
 */
export function unblockUser(id: string) {
  return httpService.post(
    `/api/code-community-forum/api/admin/poster/unblock/${id}`,
  );
}

/**
 * 设置讨论置顶/取消置顶
 */
export function updatePosterTop(id: number, top: boolean) {
  return httpService.put(
    `/api/code-community-forum/api/admin/poster/update-top`,
    {
      query: { id, top },
    },
  );
}

// 获取题解列表
// 返回 AdminProblemSolutionListVo（非 AdminPosterListVo）：后端 swagger 对本接口声明的
// 就是 Page«AdminProblemSolutionListVo»，列表页 article.service 也按该类型消费。
// 原先误标 AdminPosterListVo 会让 res.records → articleService.list 的赋值报 TS2322，
// 并使 status/statusName/label/source/refuseReason 等字段失去编译期约束。
export function getSolutionList(query: SolutionListRequestVO) {
  return httpService.get<Pagination<AdminProblemSolutionListVo>>(
    `/api/code-community-forum/api/admin/poster/problemSolutionList`,
    {
      query,
    },
  );
}

// 获取讨论列表
export function getPostList(query: PostListRequestVO) {
  return httpService.get<Pagination<AdminPosterListVo>>(
    `/api/code-community-forum/api/admin/poster/list`,
    {
      query,
    },
  );
}

// 获取讨论详情
export function getPostDetail(posterId: number, posterType = 1) {
  return httpService.get<AdminPosterDetailVo>(
    `/api/code-community-forum/api/admin/poster/detail`,
    {
      query: { posterId, posterType },
    },
  );
}

// 删除讨论
export function deletePost(posterId: number, posterType = 1) {
  return httpService.delete(
    `/api/code-community-forum/api/admin/poster/deletePoster`,
    {
      query: { posterId, posterType },
    },
  );
}

// 恢复讨论
export function recoverPost(posterId: number, posterType = 1) {
  return httpService.post(
    `/api/code-community-forum/api/admin/poster/recoverPoster`,
    {
      query: { posterId, posterType },
    },
  );
}

// 获取评论列表
export function getReplyList(query: ReplyListRequestVO) {
  return httpService.get<Pagination<AdminPosterReplyListVo>>(
    `/api/code-community-forum/api/admin/poster/replyList`,
    {
      query,
    },
  );
}

// 获取评论详情
export function getReplyDetail(posterReplyId: number) {
  return httpService.get<AdminPosterReplyDetailVo>(
    `/api/code-community-forum/api/admin/poster/replyDetail`,
    {
      query: { posterReplyId },
    },
  );
}

// 删除评论
export function deleteReply(
  posterId: number,
  posterReplyId: number,
  posterType = 1,
) {
  return httpService.delete(
    `/api/code-community-forum/api/admin/poster/deleteReply`,
    {
      query: { posterId, posterReplyId, posterType },
    },
  );
}

// 恢复评论
export function recoverReply(
  posterId: number,
  posterReplyId: number,
  posterType = 1,
) {
  return httpService.post(
    `/api/code-community-forum/api/admin/poster/recoverReply`,
    {
      query: { posterId, posterReplyId, posterType },
    },
  );
}

// 置顶评论
export function pinAdminReply(posterReplyId: number) {
  return httpService.post(
    `/api/code-community-forum/api/admin/poster/topReply`,
    {
      query: { posterReplyId },
    },
  );
}

// 取消置顶评论
export function unpinAdminReply(posterReplyId: number) {
  return httpService.post(
    `/api/code-community-forum/api/admin/poster/unTopReply`,
    {
      query: { posterReplyId },
    },
  );
}

// 通过评论
export function approveReply(
  posterId: number,
  posterReplyId: number,
  posterType = 1,
) {
  return httpService.post(
    `/api/code-community-forum/api/admin/poster/approveReply`,
    {
      query: { posterId, posterReplyId, posterType },
    },
  );
}

// 通过仅自己可见评论
export function recoverReplyAsPrivate(
  posterId: number,
  posterReplyId: number,
  posterType = 1,
) {
  return httpService.post(
    `/api/code-community-forum/api/admin/poster/recoverReplyAsPrivate`,
    {
      query: { posterId, posterReplyId, posterType },
    },
  );
}

// 不通过【删除】评论
export function rejectReply(
  posterId: number,
  posterReplyId: number,
  posterType = 1,
) {
  return httpService.post(
    `/api/code-community-forum/api/admin/poster/rejectReply`,
    {
      query: { posterId, posterReplyId, posterType },
    },
  );
}

// 获取举报分类枚举值
export function getAccuseCategoryOptions() {
  return httpService.get<AccuseCategoryVO[]>(
    `/api/code-community-forum/api/common/accuseCategory`,
  );
}

// 获取讨论分类枚举值
export function getPostCategoryOptions() {
  return httpService.get<CommonVO[]>(
    `/api/code-community-forum/api/common/posterCategory`,
  );
}

// 获取讨论状态枚举值
export function getPostStatusOptions() {
  return httpService.get<CommonVO[]>(
    `/api/code-community-forum/api/common/posterStatus`,
  );
}
// 获取评论状态枚举值
export function getPostReplyStatusOptions() {
  return httpService.get<CommonVO[]>(
    `/api/code-community-forum/api/common/posterReplyStatus`,
  );
}
// 获取题解状态枚举值
export function getProblemSolutionStatusOptions() {
  return httpService.get<CommonVO[]>(
    `/api/code-community-forum/api/common/problemSolutionStatus`,
  );
}
// 获取题解标签枚举值
export function getProblemSolutionLabelOptions() {
  return httpService.get<CommonVO[]>(
    `/api/code-community-forum/gwpub/admin/problemSolution/label-enums`,
  );
}

// 举报讨论
export function accusePost(data: AccusePosterVo, query: any) {
  return httpService.post<boolean>(
    `/api/code-community-forum/api/poster/accuse`,
    { body: data, query },
  );
}

// 举报评论
export function accuseComment(data: AccusePosterVo, query: any) {
  return httpService.post<boolean>(
    `/api/code-community-forum/api/poster/accuseReply`,
    { body: data, query },
  );
}

// 获取消息通知列表
export function getMyMessageList() {
  return httpService.get<UserMessageVo[]>(
    `/api/code-community-forum/api/message/myMessageList`,
  );
}

// 反馈所有消息已读
export function setMessageView(data: MessageViewVo) {
  return httpService.post(`/api/code-community-forum/api/message/messageView`, {
    body: data,
  });
}

// 反馈所有消息已读
export function setUserCcf(data: any) {
  return httpService.post(`/api/code-community/api/admin/user/set-user-ccf`, {
    body: data,
  });
}

// 获取举报列表
export function getAccuseList(query: {
  posterId: number;
  posterReplyId?: number;
  posterType: number;
}) {
  return httpService.get<AdminAccuseListVo[]>(
    `/api/code-community-forum/api/admin/accuse/list`,
    {
      query,
    },
  );
}

// 审核题解
export function auditContent(data: ProblemSolutionAuditVO) {
  return httpService.put(`/api/code-community-forum/api/admin/poster/audit`, {
    body: data,
  });
}

// 获取修改详情
export function getPosterHistoryDetail(posterHistoryId: number) {
  return httpService.get<ContentHistoryRecordDetail>(
    `/api/code-community-forum/api/admin/poster/posterHistoryDetail`,
    {
      query: { posterHistoryId },
    },
  );
}

// 获取修改历史列表
export function getPosterHistoryList(query: {
  posterId: number;
  posterType: number;
}) {
  return httpService.get<PosterHistoryRecordVO[]>(
    `/api/code-community-forum/api/admin/poster/updateHistoryList`,
    {
      query,
    },
  );
}

// 获取用户题解状态流转
export function getProblemSolutionStatusFlow(query: { solutionId: number }) {
  return httpService.get<ProblemSolutionStatusFlowVo[]>(
    `/api/code-community-forum/gwpub/admin/problemSolution/getStatusFlow`,
    {
      query,
    },
  );
}

// 题解申诉审核（申诉通过/申诉不通过，auditState 1=申诉通过 -1=申诉不通过）
export function appealAuditProblemSolution(data: ProblemSolutionAppealAuditVo) {
  return httpService.post(
    `/api/code-community-forum/gwpub/admin/problemSolution/appealAudit`,
    { body: data },
  );
}

//获取用户发帖白名单
export function getUserList(query: {
  nickname?: string;
  pageNum: number;
  pageSize: number;
  userId?: number;
}) {
  return httpService.post<Pagination<AdminUserListVo>>(
    `/api/code-community-forum/api/admin/user/list`,
    {
      query,
    },
  );
}

//导入发帖白名单
export function importDiscussWhiteName(file: File) {
  return httpService.post<boolean>(
    `/api/code-community-forum/api/admin/user/import-white-name`,
    {
      body: file,
    },
  );
}

//移除发帖白名单
export function removeDiscussWhiteName(data: { userIdList: number[] }) {
  return httpService.post<string>(
    `/api/code-community-forum/api/admin/user/remove-white-name`,
    {
      body: data,
    },
  );
}

// AI题解划线评论审核
export function auditSolutionInlineComment(data: SolutionInlineCommentAuditVo) {
  return httpService.put(
    `/api/code-community-forum/gwpub/admin/solutionInlineComment/audit`,
    {
      body: data,
    },
  );
}

// AI题解划线评论详情
export function getSolutionInlineCommentDetail(id: number) {
  return httpService.get<AdminSolutionInlineCommentDetailVo>(
    `/api/code-community-forum/gwpub/admin/solutionInlineComment/detail`,
    {
      query: { id },
    },
  );
}

// AI题解划线评论列表
export function getSolutionInlineCommentList(
  query: AdminSolutionInlineCommentQuery,
) {
  return httpService.get<Pagination<AdminSolutionInlineCommentListVo>>(
    `/api/code-community-forum/gwpub/admin/solutionInlineComment/list`,
    {
      query,
    },
  );
}

// AI题解划线评论审核状态枚举
export function getSolutionInlineCommentAuditStateEnum() {
  return httpService.get<Record<number, string>>(
    `/api/code-community-forum/gwpub/admin/solutionInlineComment/auditStateEnum`,
  );
}

// AI题解划线评论上一条/下一条导航
export function getSolutionInlineCommentPrevNext(
  id: number,
  query?: Omit<AdminSolutionInlineCommentQuery, 'id'>,
) {
  return httpService.get<AdminSolutionInlineCommentPrevNextVo>(
    `/api/code-community-forum/gwpub/admin/solutionInlineComment/prevNext`,
    {
      query: { ...query, id },
    },
  );
}

// 举报处置-举报通过（下线内容+记入举报列表+站内信）
export function reportAccusePass(
  posterId: number,
  posterType: number,
  posterReplyId?: number,
) {
  return httpService.post(
    `/api/code-community-forum/gwpub/admin/accuse/reportPass`,
    {
      query: { posterId, posterType, posterReplyId },
    },
  );
}

// 举报处置-举报不通过（驳回）
export function reportAccuseReject(
  posterId: number,
  posterType: number,
  posterReplyId?: number,
) {
  return httpService.post(
    `/api/code-community-forum/gwpub/admin/accuse/reportReject`,
    {
      query: { posterId, posterType, posterReplyId },
    },
  );
}

// AI题解划线评论举报列表
export function getSolutionInlineCommentAccuseList(
  commentId: number,
  accuseStatus?: number,
) {
  return httpService.get<AdminAccuseListVo[]>(
    `/api/code-community-forum/gwpub/admin/solutionInlineComment/accuseList`,
    {
      query: { commentId, accuseStatus },
    },
  );
}
